import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Disc3, Music2, Play, Video, Instagram, Facebook, Globe, Radio, Youtube, Twitter, Music } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FollowButton from "../components/FollowButton";
import VerifiedBadge from "../components/VerifiedBadge";
import { useAudioPlayer, setRadioScope } from "../hooks/useAudioPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = ["Músicas", "Álbuns", "Playlists", "Vídeos", "Sobre"];

export default function ArtistProfilePage() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Músicas");
  const { playingId, handlePlay } = useAudioPlayer();

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/artists/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setArtist)
      .catch(() => setArtist(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto">
          <div className="animate-pulse h-72 bg-zinc-900 rounded-2xl" />
        </main>
      </div>
    );
  }
  if (!artist) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Header />
        <main className="pt-32 pb-16 px-6 max-w-3xl mx-auto text-center">
          <h1 className="text-3xl font-bold mb-3">Artista não encontrado</h1>
          <p className="text-zinc-400 mb-6">Talvez o link esteja errado ou o artista ainda não foi cadastrado.</p>
          <Link to="/artistas" className="px-5 py-2 bg-[#FF007F] text-white rounded-full font-bold inline-block">
            Ver todos os artistas
          </Link>
        </main>
      </div>
    );
  }

  // Build a playable queue from the artist's musics. We augment each item with
  // `_source_cd_id` and full absolute URLs so the shared player can also treat
  // this as a "CD-like" queue for stats + preload.
  const buildArtistQueue = () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    return (artist?.musics || [])
      .map((m) => {
        const src = m.preview_url || m.audio_url || "";
        return {
          ...m,
          preview_url: src && !src.startsWith("http") ? `${apiBase}${src}` : src,
          artist_name: m.artist_name || artist?.name,
          artist_image:
            m.album_cover && !m.album_cover.startsWith("http")
              ? `${apiBase}${m.album_cover}`
              : m.album_cover || (artist?.photo_url && !artist.photo_url.startsWith("http") ? `${apiBase}${artist.photo_url}` : artist?.photo_url) || null,
          _source_cd_id: m._source_cd_id || null,
        };
      })
      .filter((m) => m.preview_url);
  };

  // "Rádio do artista": toca o catálogo INTEIRO do artista em ordem.
  // Quando o catálogo acabar, o player transiciona automaticamente pra rádio geral
  // (para o usuário não sair da plataforma).
  const playRadio = () => {
    const queue = buildArtistQueue();
    if (queue.length === 0) return;
    setRadioScope({ type: "artist", artistName: artist.name });
    handlePlay(queue[0], queue);
  };

  // Clicar em uma música específica do artista → toca a partir daquela música,
  // com o resto do catálogo do artista na queue.
  const playFromMusic = (music) => {
    const queue = buildArtistQueue();
    const startIdx = Math.max(0, queue.findIndex((q) => q.id === music.id));
    setRadioScope({ type: "artist", artistName: artist.name });
    handlePlay(queue[startIdx] || queue[0], queue);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      <main>
        {/* Hero */}
        <section
          className="relative pt-32 pb-12 px-4 sm:px-8 lg:px-12 bg-gradient-to-b from-[#5b0030] via-zinc-950 to-black"
        >
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-end gap-8">
            <div className="w-44 h-44 sm:w-56 sm:h-56 rounded-full overflow-hidden bg-zinc-800 shadow-2xl shadow-[#FF007F]/30 ring-4 ring-[#FF007F]/40 flex-shrink-0 mx-auto md:mx-0">
              {artist.photo_url ? (
                <img src={artist.photo_url.startsWith("http") ? artist.photo_url : `${process.env.REACT_APP_BACKEND_URL}${artist.photo_url}`} alt={artist.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-6xl font-black text-zinc-500">
                  {artist.name?.charAt(0) || "?"}
                </div>
              )}
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="uppercase tracking-[0.25em] text-xs text-zinc-400 mb-2">Artista</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black mb-3 flex items-center justify-center md:justify-start gap-2 flex-wrap">
                <span data-testid="artist-name">{artist.name}</span>
                {artist.verified && <VerifiedBadge size={28} />}
              </h1>
              <p className="text-zinc-400 text-sm mb-4">
                <span data-testid="artist-followers">{artist.followers_count?.toLocaleString("pt-BR") || 0}</span> seguidores
                {artist.genres?.length > 0 && <> • {artist.genres.slice(0, 3).join(" · ")}</>}
                {artist.city && <> • {artist.city}</>}
              </p>
              <div className="flex items-center gap-3 justify-center md:justify-start flex-wrap">
                <button
                  onClick={playRadio}
                  disabled={!artist.musics?.length}
                  className="flex items-center gap-2 px-6 py-3 bg-[#FF007F] hover:bg-[#FF3399] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-full transition-all hover:scale-105"
                  data-testid="artist-radio-btn"
                >
                  <Radio size={18} />
                  Rádio do artista
                </button>
                <FollowButton artistName={artist.name} size="lg" slug={artist.slug} />
                {artist.social?.instagram && (
                  <a href={artist.social.instagram} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:border-[#FF007F] hover:text-[#FF007F]">
                    <Instagram size={18} />
                  </a>
                )}
                {artist.social?.facebook && (
                  <a href={artist.social.facebook} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:border-blue-400 hover:text-blue-400">
                    <Facebook size={18} />
                  </a>
                )}
                {artist.social?.website && (
                  <a href={artist.social.website} target="_blank" rel="noopener noreferrer" className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:border-emerald-400 hover:text-emerald-400">
                    <Globe size={18} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <div className="sticky top-16 z-30 border-b border-white/10 bg-black/80 backdrop-blur">
          <div role="tablist" className="max-w-7xl mx-auto flex gap-2 px-4 sm:px-8 overflow-x-auto no-scrollbar">
            {TABS.map((t) => (
              <button
                key={t}
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                data-testid={`artist-tab-${t.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`}
                className={`shrink-0 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 ${
                  tab === t ? "border-[#FF007F] text-white" : "border-transparent text-zinc-500 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <section className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
          {tab === "Músicas" && <MusicsTab musics={artist.musics} playingId={playingId} onPlay={playFromMusic} />}
          {tab === "Álbuns" && <AlbumsTab cds={artist.cds} />}
          {tab === "Playlists" && <PlaylistsTab playlists={artist.playlists} />}
          {tab === "Vídeos" && <VideosTab videos={artist.videos} />}
          {tab === "Sobre" && <AboutTab artist={artist} />}
        </section>
      </main>
      <Footer />
    </div>
  );
}

// Compact number formatter for mobile (1.2M, 153k, 5.3k)
function formatPlays(n) {
  const num = Number(n) || 0;
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1).replace(".", ",") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1).replace(".", ",") + "k";
  return num.toLocaleString("pt-BR");
}

function MusicsTab({ musics, playingId, onPlay }) {
  if (!musics?.length) return <Empty icon={<Music2 size={32} />} text="Sem músicas catalogadas." />;
  return (
    <ol className="divide-y divide-white/5 border border-white/10 rounded-2xl overflow-hidden">
      {musics.slice(0, 50).map((m, i) => {
        const isPlaying = playingId === m.id;
        return (
          <li key={`${m.id}-${i}`} className="grid grid-cols-[28px_36px_1fr_auto] sm:grid-cols-[40px_40px_1fr_auto] items-center gap-2 sm:gap-3 px-2 sm:px-3 py-3 hover:bg-white/5 transition">
            <span className="text-zinc-500 text-xs sm:text-sm tabular-nums text-right">{String(i + 1).padStart(2, "0")}</span>
            <button
              onClick={() => onPlay(m)}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#FF007F]/20 hover:bg-[#FF007F] text-[#FF007F] hover:text-white flex items-center justify-center transition-colors"
              aria-label="Tocar"
              data-testid={`artist-track-play-${i}`}
            >
              <Play size={16} fill={isPlaying ? "currentColor" : "none"} />
            </button>
            <div className="min-w-0">
              <p className="font-semibold text-white truncate text-sm sm:text-base">{m.title || "Sem título"}</p>
              <div className="flex items-center gap-2 text-[11px] sm:text-xs text-zinc-500">
                {m.album_name && <span className="truncate">{m.album_name}</span>}
                {m.album_name && <span className="opacity-50">•</span>}
                <span className="flex items-center gap-1 tabular-nums shrink-0">
                  <Play size={10} className="text-[#FF007F]/70" fill="currentColor" />
                  {formatPlays(m.plays ?? m.play_count ?? 0)}
                </span>
              </div>
            </div>
            <span className="hidden sm:flex text-xs text-zinc-400 tabular-nums items-center gap-1 pr-2">
              {(m.plays ?? m.play_count ?? 0).toLocaleString("pt-BR")} <span className="text-zinc-600">plays</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function AlbumsTab({ cds }) {
  if (!cds?.length) return <Empty icon={<Disc3 size={32} />} text="Nenhum álbum/CD ainda." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
      {cds.map((c, i) => (
        <Link key={`${c.id}-${i}`} to={`/cd/${c.id}`} className="group">
          <div className="aspect-square overflow-hidden rounded-xl bg-zinc-900 border border-white/5 group-hover:border-[#FF007F]/50 transition">
            {c.cover_url ? (
              <img src={c.cover_url.startsWith("http") ? c.cover_url : `${process.env.REACT_APP_BACKEND_URL}${c.cover_url}`} alt={c.cd_title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-700"><Disc3 size={48} /></div>
            )}
          </div>
          <p className="mt-2 text-sm font-bold text-white truncate">{c.cd_title || "Álbum"}</p>
          <p className="text-xs text-zinc-500">{c.tracks?.length || 0} faixas</p>
        </Link>
      ))}
    </div>
  );
}

function PlaylistsTab({ playlists }) {
  if (!playlists?.length) return <Empty icon={<Music2 size={32} />} text="Sem playlists relacionadas." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
      {playlists.map((p, i) => (
        <Link key={`${p.id}-${i}`} to={`/playlist/${p.id}`} className="group">
          <div className="aspect-square overflow-hidden rounded-xl bg-zinc-900 border border-white/5 group-hover:border-[#FF007F]/50 transition">
            {p.cover_image ? (
              <img src={p.cover_image.startsWith("http") ? p.cover_image : `${process.env.REACT_APP_BACKEND_URL}${p.cover_image}`} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-700"><Music2 size={48} /></div>
            )}
          </div>
          <p className="mt-2 text-sm font-bold text-white truncate">{p.name}</p>
          <p className="text-xs text-zinc-500">{p.music_ids?.length || 0} músicas</p>
        </Link>
      ))}
    </div>
  );
}

function VideosTab({ videos }) {
  if (!videos?.length) return <Empty icon={<Video size={32} />} text="Sem vídeos por enquanto." />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
      {videos.map((v, i) => (
        <Link key={`${v.id}-${i}`} to="/vitrine" className="block">
          <div className="aspect-[9/16] overflow-hidden rounded-xl bg-zinc-900 relative">
            {v.thumbnail_url ? (
              <img src={v.thumbnail_url.startsWith("http") ? v.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}`} alt={v.title} className="w-full h-full object-cover" loading="lazy" decoding="async" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-700"><Video size={36} /></div>
            )}
          </div>
          <p className="mt-2 text-xs text-white truncate">{v.title}</p>
        </Link>
      ))}
    </div>
  );
}

function SocialLinks({ artist }) {
  // Suporta dois formatos: artist.instagram OU artist.social.instagram
  const sm = artist.social || {};
  const get = (k) => ((artist[k] || sm[k]) || "").trim();
  const ig = get("instagram");
  const yt = get("youtube");
  const tk = get("tiktok");
  const tw = get("twitter");
  const fb = get("facebook");
  const site = get("website");
  const sp = get("spotify");

  const buildUrl = (raw, kind) => {
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    const handle = raw.replace(/^@/, "").trim();
    if (kind === "instagram") return `https://instagram.com/${handle}`;
    if (kind === "youtube") return handle.startsWith("@") ? `https://youtube.com/${handle}` : `https://youtube.com/@${handle}`;
    if (kind === "tiktok") return `https://tiktok.com/@${handle}`;
    if (kind === "twitter") return `https://x.com/${handle}`;
    if (kind === "facebook") return `https://facebook.com/${handle}`;
    if (kind === "spotify") return raw.startsWith("/") ? `https://open.spotify.com${raw}` : `https://open.spotify.com/artist/${raw}`;
    return `https://${raw.replace(/^\/+/, "")}`;
  };

  const items = [
    { key: "instagram", url: buildUrl(ig, "instagram"), icon: Instagram, label: "Instagram", color: "hover:bg-pink-500 hover:border-pink-500" },
    { key: "youtube", url: buildUrl(yt, "youtube"), icon: Youtube, label: "YouTube", color: "hover:bg-red-600 hover:border-red-600" },
    { key: "tiktok", url: buildUrl(tk, "tiktok"), icon: Music, label: "TikTok", color: "hover:bg-black hover:border-zinc-200" },
    { key: "twitter", url: buildUrl(tw, "twitter"), icon: Twitter, label: "X (Twitter)", color: "hover:bg-zinc-800 hover:border-white" },
    { key: "facebook", url: buildUrl(fb, "facebook"), icon: Facebook, label: "Facebook", color: "hover:bg-blue-600 hover:border-blue-600" },
    { key: "spotify", url: buildUrl(sp, "spotify"), icon: Music2, label: "Spotify", color: "hover:bg-green-600 hover:border-green-600" },
    { key: "website", url: site ? buildUrl(site, "website") : null, icon: Globe, label: "Site", color: "hover:bg-[#FF007F] hover:border-[#FF007F]" },
  ].filter((it) => it.url);

  if (items.length === 0) return null;

  return (
    <div className="mb-6">
      <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold mb-2">Redes Sociais</p>
      <div className="flex flex-wrap gap-2">
        {items.map(({ key, url, icon: Icon, label, color }) => (
          <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={label}
            className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/20 bg-white/5 text-white text-sm font-semibold transition-all ${color}`}
            data-testid={`artist-social-${key}`}
          >
            <Icon size={16} /> <span className="hidden sm:inline">{label}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function AboutTab({ artist }) {
  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-bold mb-4">Sobre {artist.name}</h2>
      <p className="text-zinc-300 leading-relaxed mb-6 whitespace-pre-line">
        {artist.bio || "Biografia ainda não cadastrada."}
      </p>
      <SocialLinks artist={artist} />
      <dl className="grid grid-cols-2 gap-4 text-sm">
        {artist.city && (<><dt className="text-zinc-500">Cidade</dt><dd className="text-white">{artist.city}</dd></>)}
        {artist.genres?.length > 0 && (<><dt className="text-zinc-500">Gêneros</dt><dd className="text-white">{artist.genres.join(", ")}</dd></>)}
        <dt className="text-zinc-500">Seguidores</dt>
        <dd className="text-white">{artist.followers_count?.toLocaleString("pt-BR") || 0}</dd>
        <dt className="text-zinc-500">Verificado</dt>
        <dd className="text-white flex items-center gap-1">{artist.verified ? <><VerifiedBadge size={14}/> Sim</> : "Não"}</dd>
      </dl>
    </div>
  );
}

function Empty({ icon, text }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-zinc-600 gap-3">
      {icon}
      <p className="text-sm">{text}</p>
    </div>
  );
}
